import { ProductAvailabilityTag } from "@loomstateorg/shared-frontend/components/product-availability/types";
import {
    getTagConfig,
    DEFAULT_TAG_CONFIG,
} from "@loomstateorg/shared-frontend/components/product-availability/config";

export interface ProductAvailability {
    isPurchasable: boolean;
    notifyStock: boolean;
}

/**
 * Business rules for product availability tags are now driven by configuration.
 * Each tag type has its own purchasable and notifyStock settings in the config.
 *
 * Priority rules:
 * - If any tag blocks purchasing (purchasable: false), the product is not purchasable
 * - If any tag requires stock notification (notifyStock: true), notification feature is enabled
 */
export const getProductAvailability = (
    tags?: ProductAvailabilityTag[],
): ProductAvailability => {
    // Default state when no tags are present
    if (!tags?.length) {
        return {
            isPurchasable: DEFAULT_TAG_CONFIG.purchasable,
            notifyStock: DEFAULT_TAG_CONFIG.notifyStock,
        };
    }

    let isPurchasable = true;
    let notifyStock = false;

    // Process each tag and apply its business rules
    for (const tag of tags) {
        const config = getTagConfig(tag.key);

        if (config) {
            // If any tag blocks purchasing, the product becomes non-purchasable
            if (!config.purchasable) {
                isPurchasable = false;
            }

            // If any tag requires stock notification, enable notification feature
            if (config.notifyStock) {
                notifyStock = true;
            }
        }
    }

    return {
        isPurchasable,
        notifyStock,
    };
};

/**
 * Helper function to check if a product is purchasable
 */
export const isProductPurchasable = (
    tags?: ProductAvailabilityTag[],
): boolean => {
    return getProductAvailability(tags).isPurchasable;
};

/**
 * Helper function to check if stock notifications should be enabled
 */
export const shouldNotifyStock = (tags?: ProductAvailabilityTag[]): boolean => {
    return getProductAvailability(tags).notifyStock;
};
